import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { ShieldAlert, CheckCircle2 } from 'lucide-react';
import { theme, cardStyle } from '../../theme';
import DeviceApprovalModal from './DeviceApprovalModal';

/**
 * Compact security widget for the dashboard. Polls /alerts/summary every 30s.
 * Surfaces pending device-approval requests inline with one-click "Review".
 */
export default function SecurityAlertsWidget({ API_BASE, onNavigate }) {
    const [unseen, setUnseen] = useState(0);
    const [pendingDevices, setPendingDevices] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const [s, devs] = await Promise.all([
                axios.get(`${API_BASE}/security/alerts/summary`).catch(()=>({data:{unseen:0}})),
                axios.get(`${API_BASE}/security/devices`).catch(()=>({data:[]})),
            ]);
            setUnseen(s.data.unseen || 0);
            setPendingDevices((devs.data || []).filter(d => d.status === 'pending'));
        } catch {}
    }, [API_BASE]);

    useEffect(() => {
        refresh();
        const iv = setInterval(refresh, 30000);
        return () => clearInterval(iv);
    }, [refresh]);

    if (unseen === 0 && pendingDevices.length === 0) return null;

    const isPending = pendingDevices.length > 0;
    const color = isPending ? theme.danger : theme.warning;

    return (
        <>
            <div style={{
                ...cardStyle, marginBottom:'18px',
                borderLeft: `5px solid ${color}`,
                backgroundColor: isPending ? '#fff5f5' : '#fffbeb',
            }}>
                <div style={{display:'flex', alignItems:'center', gap:'14px'}}>
                    <div style={{
                        width:'44px', height:'44px', borderRadius:'12px',
                        backgroundColor: color + '22',
                        display:'flex', alignItems:'center', justifyContent:'center',
                    }}>
                        <ShieldAlert size={22} color={color}/>
                    </div>
                    <div style={{flex:1}}>
                        <div style={{fontWeight:'700', color:theme.navy, fontSize:'0.95rem'}}>
                            {isPending && (
                                <span>{pendingDevices.length} new device{pendingDevices.length>1?'s':''} requesting access</span>
                            )}
                            {!isPending && unseen > 0 && (
                                <span>{unseen} unread security alert{unseen>1?'s':''}</span>
                            )}
                        </div>
                        <div style={{fontSize:'0.8rem', color:theme.textMuted, marginTop:'2px'}}>
                            {isPending ? 'Review and approve or reject below.' : 'Open Security to review.'}
                        </div>
                    </div>
                    <button onClick={() => onNavigate && onNavigate('security')}
                        style={{padding:'8px 16px', borderRadius:'8px', border:'none', backgroundColor:theme.primary, color:'white', cursor:'pointer', fontWeight:'700', fontSize:'0.82rem'}}>
                        Open Security →
                    </button>
                </div>

                {pendingDevices.length > 0 && (
                    <div style={{marginTop:'14px', display:'flex', flexDirection:'column', gap:'8px'}}>
                        {pendingDevices.slice(0,5).map(d => (
                            <div key={d._id} style={{
                                display:'grid', gridTemplateColumns:'1.4fr 1.6fr auto', gap:'10px', alignItems:'center',
                                padding:'10px 14px', backgroundColor:'white', borderRadius:'8px', border:`1px solid ${theme.borderLight}`,
                            }}>
                                <div>
                                    <div style={{fontWeight:'700', color:theme.navy, fontSize:'0.88rem'}}>{d.name}</div>
                                    <div style={{fontSize:'0.74rem', color:theme.textMuted}}>{d.requesterName} · {d.requesterMobile}</div>
                                </div>
                                <div style={{fontSize:'0.78rem', color:theme.textMuted}}>
                                    {d.requestReason || <em style={{color:'#cbd5e1'}}>No reason provided</em>}
                                </div>
                                <button onClick={()=>setSelectedDevice(d)}
                                    style={{padding:'6px 14px', backgroundColor:theme.primary, color:'white', border:'none', borderRadius:'6px', cursor:'pointer', fontSize:'0.78rem', fontWeight:'700'}}>
                                    Review
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {selectedDevice && (
                <DeviceApprovalModal
                    API_BASE={API_BASE}
                    device={selectedDevice}
                    onClose={()=>setSelectedDevice(null)}
                    onDone={()=>{ setSelectedDevice(null); refresh(); }}
                />
            )}
        </>
    );
}
